export type DirectionKey = "w" | "a" | "s" | "d" | "arrowup" | "arrowdown" | "arrowleft" | "arrowright";

const DIRECTION_KEYS = new Set<DirectionKey>([
  "w",
  "a",
  "s",
  "d",
  "arrowup",
  "arrowdown",
  "arrowleft",
  "arrowright",
]);

export type MoveSource = "keyboard" | "pointer" | "queue";

export interface KeyboardCallbacks {
  // Returns the cooldown (ms) the keyboard should wait before the next tick,
  // based on what was actually moved (post wall-slide). Returns null when the
  // intent was blocked so the keyboard can re-fire immediately on the next frame.
  onMoveTick: (dx: -1 | 0 | 1, dy: -1 | 0 | 1, source: MoveSource) => number | null;
  isInputBlocked: () => boolean;
  getFallbackDirection?: () =>
    | { dx: -1 | 0 | 1; dy: -1 | 0 | 1; source: "pointer" | "queue" }
    | null;
}

export class KeyboardController {
  private readonly down = new Set<DirectionKey>();
  private rafId = 0;
  private nextMoveAt = 0;
  private readonly handleKeyDown = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase() as DirectionKey;
    if (!DIRECTION_KEYS.has(k)) return;
    if (this.callbacks.isInputBlocked()) return;
    this.down.add(k);
  };
  private readonly handleKeyUp = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase() as DirectionKey;
    this.down.delete(k);
  };
  private readonly handleBlur = (): void => {
    this.down.clear();
  };

  constructor(private readonly callbacks: KeyboardCallbacks) {}

  start(): void {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.handleBlur);
    this.tick();
  }

  stop(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    this.down.clear();
  }

  private tick = (): void => {
    this.rafId = requestAnimationFrame(this.tick);
    if (this.callbacks.isInputBlocked()) {
      this.down.clear();
      return;
    }
    const now = performance.now();
    if (now < this.nextMoveAt) return;

    let dx = 0;
    let dy = 0;
    if (this.down.has("w") || this.down.has("arrowup")) {
      dx -= 1;
      dy -= 1;
    }
    if (this.down.has("s") || this.down.has("arrowdown")) {
      dx += 1;
      dy += 1;
    }
    if (this.down.has("a") || this.down.has("arrowleft")) {
      dx -= 1;
      dy += 1;
    }
    if (this.down.has("d") || this.down.has("arrowright")) {
      dx += 1;
      dy -= 1;
    }
    let cdx = Math.sign(dx) as -1 | 0 | 1;
    let cdy = Math.sign(dy) as -1 | 0 | 1;
    let source: MoveSource = "keyboard";

    if (cdx === 0 && cdy === 0 && this.callbacks.getFallbackDirection) {
      const fallback = this.callbacks.getFallbackDirection();
      if (fallback) {
        cdx = fallback.dx;
        cdy = fallback.dy;
        source = fallback.source;
      }
    }

    if (cdx === 0 && cdy === 0) return;
    const cooldown = this.callbacks.onMoveTick(cdx, cdy, source);
    if (cooldown !== null) this.nextMoveAt = now + cooldown;
  };
}
